import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import back_app from '../../lib/back_app';
import { useAuthStore } from '../../store/authStore';

type BrandInfo = {
  company_name: string;
  logo_url?: string;
};

export default function BrandHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: auth0_logout } = useAuth0();
  const { user } = useAuthStore();
  const [brandInfo, setBrandInfo] = useState<BrandInfo>({
    company_name: 'My Brand',
    logo_url: undefined
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [_loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const orgs = user?.orgs || {};
  const org_ids = Object.keys(orgs);
  const has_multi_orgs = org_ids.length > 1;
  const current_org_id = user?.org_id_select || user?.org_id || '';

  const switchOrg = async (org_id: string) => {
    if (switching || org_id == current_org_id)
      return;
    setSwitching(true);
    try {
      const res = await back_app.user_set('org_id_select', org_id);
      if (res?.err && res.err != 'org_id_select_already') {
        console.error('switch org failed', res.err);
        setSwitching(false);
        return;
      }
      window.location.reload();
    } catch (err) {
      console.error('switch org failed', err);
      setSwitching(false);
    }
  };

  useEffect(() => {
    fetchBrandInfo();
  }, []);

  const fetchBrandInfo = async () => {
    try {
      const result = await back_app.bprofile_get();
      if (!result.err && result) {
        setBrandInfo({
          company_name: result.company_name || 'My Brand',
          logo_url: result.logo_url
        });
      } else {
        setBrandInfo({
          company_name: 'My Brand',
          logo_url: undefined
        });
      }
    } catch (error) {
      console.error('Error fetching brand info:', error);
      setBrandInfo({
        company_name: 'My Brand',
        logo_url: undefined
      });
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    auth0_logout({logoutParams: {returnTo: window.location.origin}});
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {}
          <img
            src="/logo_sponly_color.svg"
            alt="Sponly"
            onClick={() => navigate('/brand/dashboard')}
            className="h-[28.16px] sm:h-[31.68px] w-auto cursor-pointer"
          />

          {}
          <nav className="hidden lg:flex items-center gap-6">
            <button
              onClick={() => navigate('/brand/dashboard')}
              className={`font-medium transition-colors ${
                isActive('/brand/dashboard')
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600 pb-1'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate('/brand/campaigns')}
              className={`font-medium transition-colors ${
                isActive('/brand/campaigns') || isActive('/brand/create-campaign')
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600 pb-1'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Campaigns
            </button>
            <button
              onClick={() => navigate('/brand/assets')}
              className={`font-medium transition-colors ${
                isActive('/brand/assets') || isActive('/brand/upload-asset')
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600 pb-1'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Assets
            </button>
          </nav>

          {}
          <div className="hidden lg:flex items-center gap-2">
            {brandInfo.logo_url ? (
              <img
                src={brandInfo.logo_url}
                alt="Logo"
                className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                {brandInfo.company_name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="relative group">
              <button className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {brandInfo.company_name}
                <svg className="inline w-4 h-4 ml-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {}
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                {has_multi_orgs && (
                  <>
                    <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400
                      uppercase tracking-wide">
                      Switch organization
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {org_ids.map(oid => {
                        const is_current = oid == current_org_id;
                        return (
                          <button
                            key={oid}
                            disabled={switching}
                            onClick={() => switchOrg(oid)}
                            className={`w-full flex items-center justify-between px-4 py-2
                              text-left text-sm hover:bg-gray-50 transition ${
                                is_current ? 'bg-gray-50 font-medium text-gray-900'
                                  : 'text-gray-700'
                              }`}
                          >
                            <span className="truncate">{orgs[oid].lbl}</span>
                            {is_current && (
                              <svg className="w-4 h-4 text-blue-600 flex-shrink-0"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round"
                                  strokeWidth={2} d="M5 13l4 4L19 7"/>
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="border-t my-1"/>
                  </>
                )}
                <button
                  onClick={() => navigate('/brand/profile')}
                  className={`w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${has_multi_orgs ? '' : 'rounded-t-lg'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-lg"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t py-4">
            {}
            <div className="flex items-center gap-3 px-2 py-3 mb-3">
              {brandInfo.logo_url ? (
                <img
                  src={brandInfo.logo_url}
                  alt="Logo"
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                  {brandInfo.company_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-semibold text-gray-900">{brandInfo.company_name}</div>
                <div className="text-xs text-gray-500">Brand Account</div>
              </div>
            </div>

            {}
            <div className="space-y-1">
              <button
                onClick={() => handleNavClick('/brand/dashboard')}
                className={`w-full px-4 py-3 text-left font-medium rounded-lg transition-colors ${
                  isActive('/brand/dashboard')
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => handleNavClick('/brand/campaigns')}
                className={`w-full px-4 py-3 text-left font-medium rounded-lg transition-colors ${
                  isActive('/brand/campaigns') || isActive('/brand/create-campaign')
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Campaigns
              </button>
              <button
                onClick={() => handleNavClick('/brand/assets')}
                className={`w-full px-4 py-3 text-left font-medium rounded-lg transition-colors ${
                  isActive('/brand/assets') || isActive('/brand/upload-asset')
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Assets
              </button>

              {has_multi_orgs && (
                <>
                  <div className="border-t my-2"></div>
                  <div className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400
                    uppercase tracking-wide">
                    Switch organization
                  </div>
                  {org_ids.map(oid => {
                    const is_current = oid == current_org_id;
                    return (
                      <button
                        key={oid}
                        disabled={switching}
                        onClick={() => {
                          switchOrg(oid);
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left font-medium rounded-lg
                          transition-colors flex items-center justify-between ${
                            is_current ? 'bg-blue-50 text-blue-600'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <span className="truncate">{orgs[oid].lbl}</span>
                        {is_current && (
                          <svg className="w-5 h-5 flex-shrink-0"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round"
                              strokeWidth={2} d="M5 13l4 4L19 7"/>
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              <div className="border-t my-2"></div>

              <button
                onClick={() => handleNavClick('/brand/profile')}
                className="w-full px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 text-left font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}